import { useQuery } from "@tanstack/react-query";

import { assetData } from "@/api/client";
import { POLL } from "@/lib/format";

/** The ledgers, made visible: IMEI -> part -> asset -> driver on one line.
 *  Fetched once per selected device (never per record). */
export function ContextStrip({ imei }: { imei: string }) {
  const q = useQuery({
    queryKey: ["live", "context", imei],
    queryFn: () => assetData.context([imei]),
    refetchInterval: POLL.LIST,
    enabled: !!imei,
  });
  const c = q.data?.context?.[imei];
  if (!c) return null;
  const chip = (label: string, value: string, color = "var(--t-mute)") => (
    <span key={label} style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
      {label} <span className="mono" style={{ color, fontWeight: 600 }}>{value}</span>
    </span>
  );
  if (!c.registered) {
    return (
      <span className="chip" style={{ color: "var(--bad)", background: "color-mix(in srgb, var(--bad) 12%, transparent)" }}
        title="This IMEI publishes data but has no registry row — enroll it as a part (Registry → Enroll)">
        NOT ENROLLED
      </span>
    );
  }
  const bits = [];
  if (c.part) bits.push(chip(c.part.kind_label, `${c.part.serial_no}`, "var(--t-bright)"));
  if (c.asset) bits.push(chip(c.asset.asset_type === "vehicle" ? "vehicle" : "battery",
    `${c.asset.asset_id}${c.asset.label ? ` · ${c.asset.label}` : ""}`, "var(--info)"));
  if (c.driver) bits.push(chip("driver", `${c.driver.name} (${c.driver.code})`, "var(--ok)"));
  if (c.company) bits.push(chip("company", c.company));
  if (c.in_inventory) bits.push(chip("company", "inventory"));
  if (c.registered && !c.part) {
    bits.push(<span key="nopart" style={{ fontSize: 11.5, color: "var(--warn)" }}
      title="Connection exists but no part carries this IMEI — enroll the part to link it to an asset">no part</span>);
  }
  return <span style={{ display: "inline-flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>{bits}</span>;
}
